package com.vizio.redwolf.auth.api

import com.vizio.redwolf.utils.PiiEnvironment
import com.vizio.redwolf.utils.PiiEnvironmentConfig
import com.vizio.redwolf.auth.client.PiiHttpClient
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.choosePiiEnvironment
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import com.vizio.redwolf.utils.clientprovider.ClientConfigFactory
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import com.vizio.redwolf.utils.retryRequest
import io.ktor.client.statement.HttpResponse
import io.ktor.http.isSuccess
import io.ktor.client.call.body
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.js.JsExport

class PiiApi(piiEnvironment: PiiEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig = ClientConfigFactory.getClientConfig(WebApiUseCase.SDK_HEADERS)) {

    private val piiClient = PiiHttpClient(piiEnv = piiEnvironment, appConfig = appConfig, clientConfig = clientConfig)

    constructor(envConfig: PiiEnvironmentConfig, appConfig: AppConfig) : this(choosePiiEnvironment(envConfig), appConfig)
    constructor(envConfig: PiiEnvironmentConfig, appConfig: AppConfig, clientConfig: ClientConfig) : this(choosePiiEnvironment(envConfig), appConfig, clientConfig)
    constructor(envConfig: PiiEnvironmentConfig, appConfig: AppConfig, timeout: Long) : this(choosePiiEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.SDK_HEADERS, timeout))

    suspend fun requestRemovePii(accessToken: String, traceId: String? = null): PiiResult<PiiRemoveResponse> {
        return retryPiiRequest {
            val httpResult = piiClient.requestRemovePii(accessToken, traceId = traceId)
            handlePiiHttpResult(httpResult)
        }
    }

    suspend fun requestPiiStatus(accessToken: String, requestId: String, traceId: String? = null): PiiResult<PiiStatusResponse> {
        return retryPiiRequest {
            val httpResult = piiClient.requestPiiStatus(accessToken, requestId, traceId = traceId)
            handlePiiHttpResult(httpResult)
        }
    }

    private suspend inline fun <T : HttpResponse, reified R : Any> handlePiiHttpResult(httpResult: HttpResult<T>): PiiResult<R> {
        return when (httpResult) {
            is HttpResult.Success -> {
                val httpResponse: HttpResponse = httpResult.data
                val statusCode = httpResult.data.status.value
                val headers = ResponseHeaders.Valid(statusCode)
                if (httpResponse.status.isSuccess()) {
                    try {
                        val response = httpResponse.body<R>()
                        PiiResult.Success(headers, response)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        PiiResult.Error(headers, ex)
                    }
                } else {
                    try {
                        val consentFailureMessage = httpResponse.body<PiiFailureResponse>()
                        PiiResult.Failure(headers, consentFailureMessage)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        PiiResult.Error(headers, ex)
                    }
                }
            }
            is HttpResult.Error -> {
                val headers = ResponseHeaders.Invalid
                val exception = httpResult.exception
                if (exception is TimeoutException)
                    PiiResult.Timeout(headers, exception)
                else
                    PiiResult.Error(headers, exception)
            }
        }
    }

    private fun <T: Any> isPiiSuccess(result: T): Boolean {
        return result is PiiResult.Success<*>
    }

    private suspend inline fun <T: Any> retryPiiRequest(block: () -> T): T {
        return retryRequest(clientConfig, block, isSuccess = ::isPiiSuccess)
    }
}

@JsExport
sealed class PiiResult<out T : Any> {
    data class Success<out T : Any>(val headers: ResponseHeaders, val data: T) : PiiResult<T>()
    data class Failure(val headers: ResponseHeaders, val message: PiiFailureResponse) : PiiResult<Nothing>()
    data class Error(val headers: ResponseHeaders, val exception: Throwable) : PiiResult<Nothing>()
    data class Timeout(val headers: ResponseHeaders, val exception: Throwable) : PiiResult<Nothing>()

    fun <R> fold (
        onSuccess: (Success: Success<T>) -> R,
        onFailure: (Failure: Failure) -> R,
        onError: (Error: Error) -> R,
        onTimeout: (Timeout: Timeout) -> R
    ) : R {
        return when (this) {
            is Success -> onSuccess(Success(this.headers, this.data))
            is Failure -> onFailure(Failure(this.headers, this.message))
            is Error -> onError(Error(this.headers, this.exception))
            is Timeout -> onTimeout(Timeout(this.headers, this.exception))
        }
    }
}

@Serializable
@JsExport
class PiiRemoveResponse(
    @SerialName("requestId") val requestId: String,
    @SerialName("statusUrl") val statusUrl: String? = null,
)

@Serializable
@JsExport
class PiiStatusResponse(
    @SerialName("requestType") val requestType: String,
    @SerialName("serviceStatuses") val serviceStatuses: List<PiiServiceStatus> = emptyList(),
    @SerialName("status") val status: String
)

@Serializable
@JsExport
class PiiServiceStatus(
    @SerialName("serviceId") val serviceId: String,
    @SerialName("status") val status: String,
)

@Serializable
@JsExport
class PiiFailureResponse(
    @SerialName("message") val message: String,
)